import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getMyAccessStatus, getPlans, createCheckoutSession, cancelSubscription } from "@/api/subscriptions";
import { format } from "date-fns";
import type { PlanType } from "@/types";

const PLAN_ORDER: PlanType[] = ["one_week", "two_weeks", "three_months", "monthly"];

export default function Subscription() {
  const queryClient = useQueryClient();
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<PlanType | null>(null);

  const { data: accessStatus, isLoading } = useQuery({
    queryKey: ["access-status"],
    queryFn: getMyAccessStatus,
  });

  const { data: plans = [] } = useQuery({
    queryKey: ["plans"],
    queryFn: getPlans,
  });

  const cancelMutation = useMutation({
    mutationFn: cancelSubscription,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["access-status"] });
      setConfirmCancel(false);
    },
  });

  const checkoutMutation = useMutation({
    mutationFn: (planType: PlanType) => createCheckoutSession(planType),
    onSuccess: (data) => {
      window.location.href = data.checkout_url;
    },
  });

  const sub = accessStatus?.subscription;
  const sortedPlans = [...plans].sort(
    (a, b) => PLAN_ORDER.indexOf(a.type) - PLAN_ORDER.indexOf(b.type)
  );
  const otherPlans = sub
    ? sortedPlans.filter((p) => p.type !== sub.plan_type)
    : sortedPlans;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Loading…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Subscription</h1>
          <p className="text-sm text-gray-500 mt-1">Manage your plan and billing</p>
        </div>

        {/* Current plan */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 mb-8">
          <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-4">
            Current Plan
          </h2>

          {sub && accessStatus?.has_active_subscription ? (
            <>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <span className="text-xl font-bold text-gray-900">{sub.plan_name}</span>
                    <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-700">
                      Active
                    </span>
                  </div>
                  <p className="text-sm text-gray-500">
                    Started {format(new Date(sub.started_at), "d MMMM yyyy")}
                  </p>
                  {sub.expires_at ? (
                    <p className="text-sm text-gray-500">
                      Expires {format(new Date(sub.expires_at), "d MMMM yyyy")}
                    </p>
                  ) : (
                    <p className="text-sm text-gray-500">Auto-renews monthly</p>
                  )}
                </div>
              </div>

              {/* Cancel */}
              <div className="mt-6 pt-6 border-t border-gray-100">
                {!confirmCancel ? (
                  <button
                    onClick={() => setConfirmCancel(true)}
                    className="text-sm text-red-600 hover:text-red-700 font-medium"
                  >
                    Cancel subscription
                  </button>
                ) : (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                    <p className="text-sm font-semibold text-red-800 mb-1">
                      Cancel your subscription?
                    </p>
                    <p className="text-sm text-red-700 mb-4">
                      You'll keep access until{" "}
                      {sub.expires_at
                        ? format(new Date(sub.expires_at), "d MMMM yyyy")
                        : "the end of the current billing period"}
                      .
                    </p>
                    <div className="flex gap-3">
                      <button
                        onClick={() => setConfirmCancel(false)}
                        className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                      >
                        Keep subscription
                      </button>
                      <button
                        onClick={() => cancelMutation.mutate()}
                        disabled={cancelMutation.isPending}
                        className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
                      >
                        {cancelMutation.isPending ? "Cancelling…" : "Yes, cancel"}
                      </button>
                    </div>
                    {cancelMutation.isError && (
                      <p className="text-xs text-red-600 mt-2">Failed to cancel. Please try again.</p>
                    )}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="text-center py-6">
              <p className="text-gray-500 text-sm mb-4">
                {accessStatus?.has_used_free_trial
                  ? "Your free trial has been used. Choose a plan below to continue."
                  : "You don't have an active subscription."}
              </p>
              <Link
                to="/pricing"
                className="inline-block rounded-xl bg-primary-700 px-6 py-2.5 text-sm font-semibold text-white hover:bg-primary-800 transition"
              >
                View Plans
              </Link>
            </div>
          )}
        </div>

        {/* Other plans */}
        {otherPlans.length > 0 && (
          <div>
            <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-4">
              {sub ? "Switch Plan" : "Available Plans"}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {otherPlans.map((plan) => {
                const isLoading = checkoutMutation.isPending && selectedPlan === plan.type;
                return (
                  <div
                    key={plan.type}
                    className={`relative rounded-2xl border flex flex-col overflow-hidden transition-shadow hover:shadow-md ${
                      plan.is_subscription
                        ? "border-blue-500 shadow-blue-100 shadow-sm"
                        : "border-gray-200 bg-white"
                    }`}
                  >
                    {/* Card header band */}
                    <div
                      className={`px-5 pt-5 pb-4 ${
                        plan.is_subscription
                          ? "bg-gradient-to-br from-blue-600 to-blue-700 text-white"
                          : "bg-gray-50 border-b border-gray-100"
                      }`}
                    >
                      {plan.is_subscription && (
                        <span className="inline-block mb-2 text-xs font-bold uppercase tracking-widest bg-white/20 text-white rounded-full px-2.5 py-0.5">
                          Most Popular
                        </span>
                      )}
                      <div className="flex items-baseline gap-1">
                        <span className={`text-3xl font-extrabold ${plan.is_subscription ? "text-white" : "text-gray-900"}`}>
                          {plan.price_display}
                        </span>
                        {plan.is_subscription && (
                          <span className="text-blue-200 text-sm font-medium">/month</span>
                        )}
                      </div>
                      <p className={`text-sm font-semibold mt-0.5 ${plan.is_subscription ? "text-blue-100" : "text-gray-700"}`}>
                        {plan.name}
                      </p>
                    </div>

                    {/* Card body */}
                    <div className="px-5 py-4 flex flex-col flex-1 bg-white">
                      <ul className="space-y-2 mb-5 flex-1">
                        <li className="flex items-center gap-2 text-sm text-gray-600">
                          <svg className="h-4 w-4 text-green-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                          </svg>
                          All mock exams
                        </li>
                        <li className="flex items-center gap-2 text-sm text-gray-600">
                          <svg className="h-4 w-4 text-green-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                          </svg>
                          Category practice
                        </li>
                        <li className="flex items-center gap-2 text-sm text-gray-600">
                          <svg className="h-4 w-4 text-green-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                          </svg>
                          Traffic signs quiz
                        </li>
                        <li className="flex items-center gap-2 text-sm text-gray-600">
                          <svg className={`h-4 w-4 shrink-0 ${plan.is_subscription ? "text-blue-500" : "text-green-500"}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            {plan.is_subscription ? (
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            ) : (
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                            )}
                          </svg>
                          {plan.is_subscription ? "Auto-renews monthly" : "One-time payment"}
                        </li>
                      </ul>

                      <button
                        onClick={() => {
                          setSelectedPlan(plan.type);
                          checkoutMutation.mutate(plan.type);
                        }}
                        disabled={checkoutMutation.isPending}
                        className={`w-full rounded-xl py-2.5 text-sm font-semibold transition disabled:opacity-60 ${
                          plan.is_subscription
                            ? "bg-blue-600 text-white hover:bg-blue-700"
                            : "bg-gray-900 text-white hover:bg-gray-800"
                        }`}
                      >
                        {isLoading ? "Redirecting…" : sub ? "Switch to this plan" : "Get Started"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <p className="text-center text-xs text-gray-400 mt-8">
          Payments are processed securely by Stripe.
        </p>
      </div>
    </div>
  );
}
